import { useState, useEffect, type FormEvent, type MouseEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Brand } from '../../../components/Brand';
import { Seo } from '../../../lib/seo';
import { useAuth } from '../../../lib/auth-context';

import './Module.scss';

/* ==========================================================================
   PÁGINA DE AUTENTICAÇÃO ADMINISTRATIVA (AdminLoginPage)
   ========================================================================== */

export function AdminLoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Estados dos inputs e interface
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Efeito interativo do gradiente de fundo que segue a posição do cursor
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
  };

  // Carrega o e-mail previamente salvo caso o usuário tenha marcado "Lembrar login"
  useEffect(() => {
    const savedEmail = localStorage.getItem('a3gs_admin_remember_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Redireciona se o usuário já estiver logado
  if (user) {
    return <Navigate to="/admin" replace />;
  }

  // Ação de envio do formulário de login
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (rememberMe) {
        localStorage.setItem('a3gs_admin_remember_email', email);
      } else {
        localStorage.removeItem('a3gs_admin_remember_email');
      }

      await login(email, password);

      const destination = location.state?.from?.pathname || '/admin';
      navigate(destination, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível entrar.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Seo
        title="Administração"
        description="Área administrativa da A3GS Tec."
        path="/admin/login"
        noIndex
      />

      <main className="auth-split-wrapper">
        {/* Moldura do Card Bipartido com Efeito Spotlight Integrado */}
        <div className="auth-split-card" onMouseMove={handleMouseMove}>
          
          {/* Camada do Gradiente Opaco Dinâmico (Segue o Mouse) */}
          <div className="auth-card-mouse-glow" />

          {/* PAINEL LATERAL ESQUERDO: APRESENTAÇÃO E INFORMAÇÕES */}
          <div className="auth-split-banner">
            <div className="auth-banner-glow-bg" />
            <div className="auth-banner-content">
              <div className="auth-brand-wrap">
                <Brand />
              </div>

              <div className="auth-banner-text">
                <span className="auth-badge">PAINEL DE CONTROLE</span>
                <h2>Gestão digital de infraestrutura e serviços.</h2>
                <p>
                  Acesse para gerenciar conteúdos do CMS, monitorar solicitações,
                  serviços em nuvem e configurações da plataforma A3GS Tec.
                </p>
              </div>

              <div className="auth-banner-features">
                <div className="auth-feature-item">
                  <Icon icon="lucide:shield-check" width="20" height="20" className="auth-feature-icon" />
                  <span>Ambiente Autenticado & Protegido</span>
                </div>
                <div className="auth-feature-item">
                  <Icon icon="lucide:database" width="20" height="20" className="auth-feature-icon" />
                  <span>Banco Distribuído Cloudflare D1</span>
                </div>
              </div>

              <div className="auth-banner-footer">
                <small>© {new Date().getFullYear()} A3GS Tec — Todos os direitos reservados.</small>
              </div>
            </div>
          </div>

          {/* PAINEL DIREITO: FORMULÁRIO DE LOGIN */}
          <div className="auth-split-form-panel">
            <div className="auth-form-header">
              <span className="section-tag">ACESSO RESTRITO</span>
              <h1>Entrar no painel</h1>
              <p>Insira suas credenciais para gerenciar a plataforma.</p>
            </div>

            <form onSubmit={submit} className="auth-form-body">
              {/* Campo: E-mail com ícone à esquerda */}
              <label>
                E-mail
                <div className="input-icon-wrapper">
                  <Icon icon="lucide:mail" className="input-icon-left" width="18" height="18" />
                  <input
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu.email@exemplo.com"
                    disabled={loading}
                  />
                </div>
              </label>

              {/* Campo: Senha com ícone à esquerda e botão de alternância à direita */}
              <label>
                Senha
                <div className="input-icon-wrapper">
                  <Icon icon="lucide:lock" className="input-icon-left" width="18" height="18" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="input-btn-right"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Exibir senha'}
                    tabIndex={-1}
                  >
                    <Icon
                      icon={showPassword ? 'lucide:eye-off' : 'lucide:eye'}
                      width="18"
                      height="18"
                    />
                  </button>
                </div>
              </label>

              {/* Checkbox: Lembrar Login */}
              <div className="auth-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                  <span>Lembrar login</span>
                </label>
              </div>

              {/* Mensagem de Erro */}
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}

              {/* Botão de Entrar */}
              <button className="btn btn-primary btn-auth-submit" type="submit" disabled={loading}>
                {loading ? 'Autenticando…' : 'Entrar no Painel'}
              </button>
            </form>
          </div>

        </div>
      </main>
    </>
  );
}